import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import CaseContextHeader from "../components/CaseContextHeader";
import StatusBadge from "../components/StatusBadge";
import api from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Check, X, Edit3, ArrowRight } from "lucide-react";

export default function FieldValidation() {
  const { caseId } = useParams();
  const [caseDoc, setCaseDoc] = useState(null);
  const [fields, setFields] = useState([]);
  const [issues, setIssues] = useState([]);
  const [editing, setEditing] = useState({}); // id -> value

  const reload = () => {
    api.get(`/cases/${caseId}`).then((r) => setCaseDoc(r.data.case));
    api.get(`/cases/${caseId}/extracted-fields`).then((r) => setFields(r.data));
    api.get(`/cases/${caseId}/validation-issues`).then((r) => setIssues(r.data));
  };
  useEffect(reload, [caseId]);

  const updateField = async (f, status, value) => {
    const payload = { review_status: status };
    if (value !== undefined) payload.field_value = value;
    await api.patch(`/extracted-fields/${f.extracted_field_id}`, payload);
    toast.success(`Field ${f.field_name} → ${status}`);
    reload();
  };

  const markReady = async () => {
    try {
      await api.post(`/cases/${caseId}/mark-ready-for-drafting`);
      toast.success("Marked ready for drafting");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <Layout breadcrumbs={[
      { label: "SSRD", to: "/dashboard" },
      { label: "Cases", to: "/cases" },
      caseDoc ? { label: `${caseDoc.case_number}/${caseDoc.case_year}`, to: `/cases/${caseId}` } : "…",
      "Field Validation",
    ]}>
      <CaseContextHeader caseDoc={caseDoc} />

      <Card className="mb-6" data-testid="extracted-fields-card"><CardContent className="p-0">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-widest text-muted-foreground uppercase">Validation</div>
            <div className="text-lg font-semibold">Extracted Fields</div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={reload}>Refresh</Button>
            <Button onClick={markReady} className="gap-2" data-testid="mark-ready-btn">
              Mark Ready for Drafting <ArrowRight className="size-4" />
            </Button>
          </div>
        </div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Category</TableHead><TableHead>Field</TableHead>
            <TableHead>Extracted Value</TableHead><TableHead>Page</TableHead>
            <TableHead>Confidence</TableHead><TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {fields.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">No extracted fields. Run document processing first.</TableCell></TableRow>}
            {fields.map((f) => (
              <TableRow key={f.extracted_field_id} data-testid={`field-row-${f.extracted_field_id}`}>
                <TableCell className="text-xs">{f.field_category}</TableCell>
                <TableCell className="text-xs font-mono">{f.field_name}</TableCell>
                <TableCell>
                  {editing[f.extracted_field_id] !== undefined ? (
                    <Input value={editing[f.extracted_field_id]} onChange={(e) => setEditing({ ...editing, [f.extracted_field_id]: e.target.value })} className="h-8" />
                  ) : (
                    <span className="gujarati text-sm">{f.field_value}</span>
                  )}
                </TableCell>
                <TableCell className="text-xs">{f.source_page_no}</TableCell>
                <TableCell className="text-xs"><span className={f.confidence_score < 0.75 ? "text-red-700 font-mono" : "font-mono"}>{f.confidence_score}</span></TableCell>
                <TableCell><StatusBadge status={f.review_status} /></TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    {editing[f.extracted_field_id] === undefined ? (
                      <>
                        <Button size="sm" variant="outline" onClick={() => updateField(f, "Accepted")} className="gap-1" data-testid={`accept-${f.extracted_field_id}`}><Check className="size-3.5 text-emerald-700" /> Accept</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditing({ ...editing, [f.extracted_field_id]: f.field_value })} className="gap-1"><Edit3 className="size-3.5" /> Edit</Button>
                        <Button size="sm" variant="outline" onClick={() => updateField(f, "Rejected")} className="gap-1" data-testid={`reject-${f.extracted_field_id}`}><X className="size-3.5 text-red-700" /> Reject</Button>
                      </>
                    ) : (
                      <>
                        <Button size="sm" onClick={() => { updateField(f, "Edited", editing[f.extracted_field_id]); setEditing({ ...editing, [f.extracted_field_id]: undefined }); }}>Save</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditing({ ...editing, [f.extracted_field_id]: undefined })}>Cancel</Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Card data-testid="validation-issues-card"><CardContent className="p-0">
        <div className="px-5 py-3 border-b border-border">
          <div className="text-[10px] tracking-widest text-muted-foreground uppercase">Validation</div>
          <div className="text-lg font-semibold">Issues</div>
        </div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Type</TableHead><TableHead>Field</TableHead>
            <TableHead>Description</TableHead><TableHead>Severity</TableHead><TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {issues.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-6">No issues detected.</TableCell></TableRow>}
            {issues.map((i) => (
              <TableRow key={i.issue_id}>
                <TableCell className="text-xs">{i.issue_type}</TableCell>
                <TableCell className="text-xs font-mono">{i.field}</TableCell>
                <TableCell className="text-xs">{i.issue_description}</TableCell>
                <TableCell><StatusBadge status={i.severity === "High" ? "Failed" : i.severity === "Medium" ? "Pending" : "Processing"} /></TableCell>
                <TableCell><StatusBadge status={i.resolution_status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </Layout>
  );
}
